/**
 * Person Tracking System
 * 
 * Tracks detected persons across frames with unique IDs,
 * analyzes movement patterns, and detects suspicious behaviors.
 */

export interface PersonBounds {
  x: number;      // Normalized 0-1
  y: number;      // Normalized 0-1
  width: number;  // Normalized 0-1
  height: number; // Normalized 0-1
  centerX: number;
  centerY: number;
}

export interface PersonPose {
  /** Person ID (stable across frames) */
  id: string;
  /** Bounding box */
  bounds: PersonBounds;
  /** Is facing camera (based on landmark visibility) */
  facingCamera: boolean;
  /** Body orientation angle (0-360 degrees, 0 = facing camera) */
  bodyOrientation: number;
  /** Confidence score (0-1) */
  confidence: number;
  /** Frame timestamp when first detected */
  firstSeenAt: number;
  /** Frame timestamp when last detected */
  lastSeenAt: number;
  /** Number of consecutive frames detected */
  frameCount: number;
  /** Movement velocity (pixels per second) */
  velocity: { x: number; y: number };
  /** Position history (last N frames) */
  positionHistory: Array<{ x: number; y: number; timestamp: number }>;
  /** Is this likely the main candidate (largest, most centered) */
  isMainCandidate: boolean;
  /** Distance from center (0-1, 0 = center) */
  distanceFromCenter: number;
  /** Relative size (0-1, 1 = largest detected) */
  relativeSize: number;
  /** Suspicion flags */
  flags: string[];
}

export interface PersonTrackingState {
  /** All currently tracked persons */
  persons: PersonPose[];
  /** Person IDs that recently left the frame */
  recentDepartures: Array<{ id: string; timestamp: number }>;
  /** Detection statistics */
  stats: {
    totalPersonsDetected: number;
    maxSimultaneousPersons: number;
    averagePersonCount: number;
  };
}

const POSITION_HISTORY_LENGTH = 10;
const PERSON_MATCH_THRESHOLD = 0.3; // IOU threshold for matching persons across frames
const DEPARTURE_RETENTION_MS = 5000; // Keep departed persons in memory for 5s
const MIN_FRAMES_FOR_SUSPICION = 15; // ~1.5s at 10fps
const BEHIND_CANDIDATE_THRESHOLD = 0.15; // Relative position threshold
const PROXY_POSITION_THRESHOLD = 0.25; // Position threshold for proxy detection

/**
 * Calculate Intersection over Union (IoU) between two bounding boxes
 */
function calculateIoU(box1: PersonBounds, box2: PersonBounds): number {
  const x1 = Math.max(box1.x, box2.x);
  const y1 = Math.max(box1.y, box2.y);
  const x2 = Math.min(box1.x + box1.width, box2.x + box2.width);
  const y2 = Math.min(box1.y + box1.height, box2.y + box2.height);

  const intersectionArea = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
  const box1Area = box1.width * box1.height;
  const box2Area = box2.width * box2.height;
  const unionArea = box1Area + box2Area - intersectionArea;

  return unionArea > 0 ? intersectionArea / unionArea : 0;
}

/**
 * Calculate distance from frame center
 */
function calculateDistanceFromCenter(bounds: PersonBounds): number {
  const centerX = 0.5;
  const centerY = 0.5;
  const dx = bounds.centerX - centerX;
  const dy = bounds.centerY - centerY;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Generate unique person ID
 */
let personIdCounter = 0;
function generatePersonId(): string {
  return `person_${Date.now()}_${++personIdCounter}`;
}

/**
 * Person Tracker Class
 * Maintains state across frames and provides tracking logic
 */
export class PersonTracker {
  private trackedPersons: Map<string, PersonPose> = new Map();
  private recentDepartures: Array<{ id: string; timestamp: number }> = [];
  private frameTimestamps: number[] = [];
  private maxPersonsCount: number = 0;
  private totalDetections: number = 0;

  /**
   * Update tracking with new detections from current frame
   */
  update(
    detections: Array<{
      bounds: PersonBounds;
      facingCamera: boolean;
      bodyOrientation: number;
      confidence: number;
    }>,
    timestamp: number
  ): PersonTrackingState {
    this.frameTimestamps.push(timestamp);
    if (this.frameTimestamps.length > 100) this.frameTimestamps.shift();

    // Sort detections by size (largest first)
    const sortedDetections = detections.sort(
      (a, b) => b.bounds.width * b.bounds.height - a.bounds.width * a.bounds.height
    );

    // Calculate relative sizes
    const maxSize = sortedDetections[0]
      ? sortedDetections[0].bounds.width * sortedDetections[0].bounds.height
      : 1;

    // Match current detections to existing tracked persons
    const matchedIds = new Set<string>();
    const updatedPersons = new Map<string, PersonPose>();

    for (const detection of sortedDetections) {
      let bestMatch: PersonPose | null = null;
      let bestIoU = 0;

      // Try to match with existing persons
      for (const [id, person] of this.trackedPersons) {
        if (matchedIds.has(id)) continue;
        const iou = calculateIoU(detection.bounds, person.bounds);
        if (iou > PERSON_MATCH_THRESHOLD && iou > bestIoU) {
          bestMatch = person;
          bestIoU = iou;
        }
      }

      let updatedPerson: PersonPose;

      if (bestMatch) {
        // Update existing person
        const velocity = {
          x: (detection.bounds.centerX - bestMatch.bounds.centerX) / 0.1, // Assume 100ms between frames
          y: (detection.bounds.centerY - bestMatch.bounds.centerY) / 0.1,
        };

        const positionHistory = [
          ...bestMatch.positionHistory,
          {
            x: detection.bounds.centerX,
            y: detection.bounds.centerY,
            timestamp,
          },
        ].slice(-POSITION_HISTORY_LENGTH);

        updatedPerson = {
          ...bestMatch,
          bounds: detection.bounds,
          facingCamera: detection.facingCamera,
          bodyOrientation: detection.bodyOrientation,
          confidence: detection.confidence,
          lastSeenAt: timestamp,
          frameCount: bestMatch.frameCount + 1,
          velocity,
          positionHistory,
          relativeSize: (detection.bounds.width * detection.bounds.height) / maxSize,
          distanceFromCenter: calculateDistanceFromCenter(detection.bounds),
        };

        matchedIds.add(bestMatch.id);
      } else {
        // New person detected
        this.totalDetections++;
        updatedPerson = {
          id: generatePersonId(),
          bounds: detection.bounds,
          facingCamera: detection.facingCamera,
          bodyOrientation: detection.bodyOrientation,
          confidence: detection.confidence,
          firstSeenAt: timestamp,
          lastSeenAt: timestamp,
          frameCount: 1,
          velocity: { x: 0, y: 0 },
          positionHistory: [
            {
              x: detection.bounds.centerX,
              y: detection.bounds.centerY,
              timestamp,
            },
          ],
          isMainCandidate: false,
          distanceFromCenter: calculateDistanceFromCenter(detection.bounds),
          relativeSize: (detection.bounds.width * detection.bounds.height) / maxSize,
          flags: [],
        };
      }

      updatedPersons.set(updatedPerson.id, updatedPerson);
    }

    // Check for departed persons
    for (const [id, person] of this.trackedPersons) {
      if (!matchedIds.has(id)) {
        this.recentDepartures.push({ id, timestamp });
      }
    }

    // Clean old departures
    this.recentDepartures = this.recentDepartures.filter(
      (d) => timestamp - d.timestamp < DEPARTURE_RETENTION_MS
    );

    // Identify main candidate (largest person closest to center)
    const persons = Array.from(updatedPersons.values());
    if (persons.length > 0) {
      const candidate = persons.reduce((best, person) => {
        const score = person.relativeSize * 0.7 - person.distanceFromCenter * 0.3;
        const bestScore = best.relativeSize * 0.7 - best.distanceFromCenter * 0.3;
        return score > bestScore ? person : best;
      });
      candidate.isMainCandidate = true;
    }

    // Analyze suspicious behaviors
    this.analyzeSuspiciousBehaviors(persons, timestamp);

    // Update stats
    this.maxPersonsCount = Math.max(this.maxPersonsCount, persons.length);

    this.trackedPersons = updatedPersons;

    return {
      persons,
      recentDepartures: this.recentDepartures,
      stats: {
        totalPersonsDetected: this.totalDetections,
        maxSimultaneousPersons: this.maxPersonsCount,
        averagePersonCount:
          this.frameTimestamps.length > 0
            ? this.totalDetections / this.frameTimestamps.length
            : 0,
      },
    };
  }

  /**
   * Analyze suspicious behaviors and set flags
   */
  private analyzeSuspiciousBehaviors(persons: PersonPose[], timestamp: number): void {
    if (persons.length === 0) return;

    const mainCandidate = persons.find((p) => p.isMainCandidate);
    const others = persons.filter((p) => !p.isMainCandidate);

    for (const other of others) {
      other.flags = [];

      // Flag: Person behind candidate
      if (mainCandidate && other.bounds.y < mainCandidate.bounds.y - BEHIND_CANDIDATE_THRESHOLD) {
        if (other.frameCount > MIN_FRAMES_FOR_SUSPICION) {
          other.flags.push("person_behind_candidate");
        }
      }

      // Flag: Person at side (holding phone/paper scenario)
      if (
        mainCandidate &&
        (Math.abs(other.bounds.centerX - mainCandidate.bounds.centerX) > PROXY_POSITION_THRESHOLD)
      ) {
        if (other.frameCount > MIN_FRAMES_FOR_SUSPICION) {
          other.flags.push("person_at_side");
        }
      }

      // Flag: Person not facing camera (suspicious positioning)
      if (!other.facingCamera && other.frameCount > MIN_FRAMES_FOR_SUSPICION) {
        other.flags.push("person_facing_away");
      }

      // Flag: Person lingers too long
      const dwellTime = timestamp - other.firstSeenAt;
      if (dwellTime > 10000 && !other.flags.includes("person_behind_candidate")) {
        other.flags.push("person_lingering");
      }

      // Flag: Rapid movement (entering/exiting quickly)
      const speed = Math.sqrt(other.velocity.x ** 2 + other.velocity.y ** 2);
      if (speed > 2 && other.frameCount > 5) {
        other.flags.push("rapid_movement");
      }
    }

    // Flag main candidate behaviors
    if (mainCandidate && others.length > 0) {
      mainCandidate.flags = [];

      // Check if candidate is looking toward another person
      const lookingAtOther = others.some((other) => {
        const angle = Math.atan2(
          other.bounds.centerY - mainCandidate.bounds.centerY,
          other.bounds.centerX - mainCandidate.bounds.centerX
        );
        const angleDeg = (angle * 180) / Math.PI;

        // If candidate body/face orientation aligns with other person's position
        const orientationMatch = Math.abs(angleDeg - mainCandidate.bodyOrientation) < 30;
        return orientationMatch;
      });

      if (lookingAtOther) {
        mainCandidate.flags.push("looking_at_other_person");
      }
    }
  }

  /**
   * Reset tracker state
   */
  reset(): void {
    this.trackedPersons.clear();
    this.recentDepartures = [];
    this.frameTimestamps = [];
    this.maxPersonsCount = 0;
    this.totalDetections = 0;
    personIdCounter = 0;
  }

  /**
   * Get current tracked persons
   */
  getPersons(): PersonPose[] {
    return Array.from(this.trackedPersons.values());
  }
}
